import React, { useEffect, useState, useRef } from "react";
import { SVG, Quantity, Button, CartUpsellWindowItem } from "~components";
import cn from "classnames";
import {
  useCartContext,
  useProductContext,
  useQuantity,
  useTrapFocus
} from "~hooks";
import * as styles from "./styles.module.scss";

const CartUpsellWindow = () => {
  const {
    isCartUpsellWindowOpen,
    setIsCartUpsellWindowOpen,
    cartUpsellProductIds: productIds,
    addVariantsToCart,
    getCartDiscountPercentage,
    numberOfItemsInCart
  } = useCartContext();

  const {
    isFetchingInventory,
    getProductData,
    getVariantData,
    getProductVariantsData,
    getVariantProductData
  } = useProductContext();

  const [products, setProducts] = useState([]);

  useEffect(() => {
    const productsArray = [];

    productIds?.forEach((productId) => {
      const { title, price, shortDescription, productPageImages } =
        getProductData(productId);
      const productVariantsData = getProductVariantsData(productId);

      const formattedVariants = productVariantsData.map((variant) => ({
        variantId: variant.variantId,
        title: variant.title,
        hex: variant.hex
      }));

      const formattedProduct = {
        productId,
        title,
        price: price.amount,
        variants: formattedVariants,
        shortDescription,
        image: productPageImages[0]
      };

      productsArray.push(formattedProduct);
    });

    setProducts(productsArray);
  }, [productIds]);

  const selectedVariantIds = products
    ?.map((item) => {
      const selectedVariants = item.variants.filter(
        (variantNode) => variantNode.selected
      );
      const variantIds = [];
      selectedVariants.forEach((variant) => variantIds.push(variant.variantId));
      return variantIds;
    })
    ?.flat();

  const containerRef = useRef();
  useTrapFocus(isCartUpsellWindowOpen, containerRef); // If open, handle in that component instead

  const { quantity, increment, decrement, setQuantity } = useQuantity();

  // Reset quantity whenever the productIds change
  useEffect(() => {
    setQuantity(1);
  }, [productIds]);

  const areAllSelectedVariantsInStock = () => {
    for (let i = 0; i < selectedVariantIds?.length; i++) {
      const variantInventoryQuantity = getVariantData(
        selectedVariantIds[i]
      ).inventoryQuantity;
      if (variantInventoryQuantity < quantity) return false;
    }
    return true;
  };

  const setSelectedVariant = (selectedVariant) => {
    const parentProductId = getVariantProductData(selectedVariant)?.productId;
    setProducts((prev) =>
      prev.map((node) =>
        node.productId === parentProductId
          ? {
              ...node,
              variants: node.variants.map((variant) =>
                variant.variantId === selectedVariant
                  ? { ...variant, selected: true }
                  : { ...variant, selected: false }
              )
            }
          : { ...node }
      )
    );
  };

  const handleAddToCart = () => {
    const allSelectedVariants = products.map(
      (product) =>
        product.variants.find((variant) => variant.selected).variantId
    );

    addVariantsToCart(allSelectedVariants, quantity);
  };

  const contentRef = useRef(null);

  useEffect(() => {
    if (isCartUpsellWindowOpen) {
      contentRef.current.scrollTo(0, 0);
    }
  }, [isCartUpsellWindowOpen]);

  const numberOfItemsInUpsellBundle = products.length;

  const numberOfItemsInCartIfTheseItemsWereAdded =
    numberOfItemsInCart + numberOfItemsInUpsellBundle * quantity;

  const discountPercentageIfTheseItemsWereAdded = getCartDiscountPercentage(
    numberOfItemsInCartIfTheseItemsWereAdded
  );

  const combinedProductsPreDiscountPrice = products.reduce(
    (acc, product) => acc + product.price,
    0
  );

  const combinedProductsDiscountedPrice =
    combinedProductsPreDiscountPrice -
    combinedProductsPreDiscountPrice *
      (discountPercentageIfTheseItemsWereAdded / 100);

  const totalPreDiscountPrice = combinedProductsPreDiscountPrice * quantity;
  const totalDiscountedPrice = combinedProductsDiscountedPrice * quantity;
  const totalSavings = totalPreDiscountPrice - totalDiscountedPrice;

  return (
    <div
      className={cn(styles.container, {
        [styles.isOpen]: isCartUpsellWindowOpen
      })}
      ref={containerRef}
    >
      <div className={styles.topBar}>
        <div className="b2">Edit Product</div>
        <div className={styles.closeButtonContainer}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => setIsCartUpsellWindowOpen(false)}
            disabled={!isCartUpsellWindowOpen}
          >
            <span className="caption">CLOSE</span>
            <SVG svg="x" className={styles.closeButtonIcon} />
          </button>
        </div>
      </div>
      <div className={styles.content} ref={contentRef}>
        {products?.map((product) => (
          <CartUpsellWindowItem
            key={product.productId}
            product={product}
            quantity={quantity}
            setSelectedVariant={setSelectedVariant}
            numberOfItemsInUpsellBundle={products.length}
          />
        ))}
        <p className={cn(`h4`, styles.totalSavings)}>
          Total savings: ${totalSavings.toFixed(2)}
        </p>
      </div>
      <div className={styles.bottomBar}>
        <Quantity
          value={quantity}
          handleIncrement={increment}
          handleDecrement={decrement}
          disabled={!isCartUpsellWindowOpen}
        />
        <Button
          className={cn(styles.addButton, `cartUpsellWindowCartButton`)}
          fluid
          strikethrough={
            totalPreDiscountPrice
              ? `$${totalPreDiscountPrice.toFixed(2)}`
              : null
          }
          onClick={handleAddToCart}
          disabled={!areAllSelectedVariantsInStock() || !isCartUpsellWindowOpen}
          loading={isFetchingInventory}
        >
          Add <span className={styles.mobileText}>To Cart </span>- $
          {totalDiscountedPrice.toFixed(2)}
        </Button>
      </div>
    </div>
  );
};

export default CartUpsellWindow;
